import TrialProgress, { TrialPlansMarker } from "@/components/TrialProgress";
import { useBillingLifecycle } from "@/lib/billingLifecycle";
import BillingPlanChange from "@/components/BillingPlanChange";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { Check, AlertTriangle, Users, Crown, Building2, HardHat, Loader2, Calendar, CreditCard, XCircle, Lock, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });

const TIER_ICONS = {
  crew: HardHat,
  site: Building2,
  enterprise: Crown,
  white_label: Crown,
};

const LOCK_TITLES = {
  trial_expired: "Your trial has ended",
  past_due_grace_exceeded: "Payment overdue — access locked",
  subscription_ended: "Subscription has ended",
  suspended: "Account suspended",
  trial_date_invalid: "Your subscription needs review",
  expired: "Subscription expired",
  cancelled: "Subscription cancelled",
  subscription_required: "Subscription required",
};

const LOCK_MESSAGES = {
  trial_expired: "Your Fieldlink trial has ended. Your company data is safe. Choose a plan to continue.",
  past_due_grace_exceeded: "Your subscription needs attention. Your company data is safe. Update your payment details to restore access.",
  subscription_ended: "Your subscription has ended. Resubscribe to restore access — all your historical data is safe.",
  suspended: "Your account has been suspended by Fieldlink support. Please contact us if you think this is a mistake.",
  trial_date_invalid: "Contact Fieldlink support to review your trial dates. Your company data is safe.",
  expired: "Your subscription has expired. Resubscribe to restore access.",
  cancelled: "Your subscription has been cancelled. Resubscribe anytime to restore full access.",
  subscription_required: "An active subscription is required to continue using Fieldlink.",
};

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export default function Billing() {
  const { user } = useAuth();
  const { refresh: refreshLifecycle } = useBillingLifecycle();
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [paying, setPaying] = useState(null); // tier id mid-checkout
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(() => {
    api.get("/billing/me").then((r) => { setData(r.data); refreshLifecycle(); }).catch(() => setData(false));
  }, [refreshLifecycle]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const target = params.get("review");
    if (data?.can_manage_billing && data.billing_management === "stripe" && ["crew", "site", "yard", "white_label"].includes(target)) {
      setReviewTarget(target);
      const next = new URLSearchParams(params); next.delete("review"); setParams(next, { replace: true });
    }
  }, [data, params, setParams]);

  useEffect(() => {
    if (data?.can_manage_billing && data.billing_management === "stripe" && data.lifecycle_state !== "active") {
      api.post("/subscription/sync").then(load).catch(() => {});
    }
    // Reconcile once when Stripe management becomes known, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.can_manage_billing, data?.billing_management]);

  // ----- Handle return from Stripe -----
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.message("Checkout cancelled — no charge made.");
      setParams({}, { replace: true });
      return;
    }
    if (!sid) return;
    setPolling(true);
    setPollMsg("Confirming your payment with Stripe…");
    let attempts = 0;
    const maxAttempts = 12; // 12 × 2s = 24s
    const poll = async () => {
      attempts += 1;
      try {
        const { data: status } = await api.get(`/subscription/status/${sid}`);
        if (["paid", "no_payment_required"].includes(status.payment_status) && status.entitlement_active) {
          setPolling(false);
          setPollMsg("");
          toast.success(`Subscription activated — welcome to the ${(status.metadata?.fieldlink_tier || "new").toUpperCase()} plan!`);
          setParams({}, { replace: true });
          load();
          return;
        }
        if (status.status === "expired") {
          setPolling(false);
          setPollMsg("");
          toast.error("Checkout session expired. Please try again.");
          setParams({}, { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          setPolling(false);
          setPollMsg("Still processing — refresh the page in a minute to see the update.");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setPolling(false);
        setPollMsg("Couldn't verify payment status. Refresh and try again.");
      }
    };
    poll();
  }, [params.get("session_id"), params.get("cancelled")]);

  if (data === null) {
    return (
      <div className="p-10 text-zinc-700 dark:text-zinc-300 text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (data === false) return <div className="p-10 text-zinc-500 text-sm">Billing isn&apos;t available for your account.</div>;

  const canManage = data.can_manage_billing;
  const periodEnd = data.current_period_end || null;
  const days = daysLeft(periodEnd);
  const cancelPending = data.plan_status === "cancel_at_period_end" || data.cancel_at_period_end;
  const lockState = data.lock_state || { locked: false };
  const lockedReason = lockState.locked ? lockState.reason : (params.get("locked") || null);
  const trialDaysLeft = lockState.trial_days_left;
  const graceDaysLeft = lockState.grace_days_left;
  const isTrialActive = data.plan === "trial" && !lockState.locked;
  const isPastDueGrace = data.plan_status === "past_due" && graceDaysLeft != null && graceDaysLeft > 0 && !lockState.locked;

  const subscribe = async (tier) => {
    if (!canManage) { toast.error("Only org admins can manage billing"); return; }
    setPaying(tier.id);
    try {
      const { data: resp } = await api.post("/subscription/checkout", {
        tier: tier.id,
        origin_url: window.location.origin,
      });
      if (resp.url) {
        window.location.href = resp.url;
      } else {
        toast.error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      load();
    } finally {
      setPaying(null);
    }
  };

  const cancelSub = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Auto-renew is off — you keep access until the period closes.");
      setCancelOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const resumeSub = async () => {
    try {
      await api.post("/subscription/resume");
      toast.success("Auto-renew is back on");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    }
  };

  const openPortal = async () => {
    try {
      const { data: resp } = await api.post("/subscription/portal", {
        return_url: `${window.location.origin}/app/billing`,
      });
      if (resp.url) window.location.href = resp.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
    }
  };

  const refreshStripe = async () => {
    try { await api.post("/subscription/sync"); load(); toast.success("Billing synchronized"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not synchronize billing"); }
  };
  const cancelDowngrade = async () => {
    try { await api.post("/subscription/downgrade/cancel"); load(); toast.success("Scheduled downgrade cancelled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel scheduled downgrade"); }
  };

  return (
    <div data-testid="billing-page" className="p-6 lg:p-10 max-w-5xl mx-auto">
      <PageHeader
        tag="/ subscription"
        title="Billing"
        subtitle={`Your plan, headcount and what the next tier looks like for ${data.org_name}.`}
      />

      <TrialProgress />
      <BillingPlanChange target={reviewTarget === "__pending__" ? null : reviewTarget} operationId={reviewTarget === "__pending__" ? data.billing_change_id : null} onClose={() => { setReviewTarget(null); load(); }} onChanged={load} />
      <div className="mb-5 space-y-2 text-sm" data-testid="billing-entitlement-summary">
        <p><strong>{data.active_users} / {data.user_limit ?? "custom"} active users</strong> · {data.billing_management === "stripe" ? `Stripe-managed · ${data.stripe_status || "awaiting sync"}` : data.billing_management === "trial" ? "Trial" : data.manual_checkout_available ? "Manual billing" : "Manual / legacy billing — contact Fieldlink for plan changes"}</p>
        {data.pending_downgrade && <p>Scheduled: <strong className="capitalize">{data.pending_downgrade.target_plan.replaceAll("_", " ")}</strong> from {new Date(data.pending_downgrade.effective_at).toLocaleDateString("en-NZ")}. {canManage && data.pending_downgrade.status === "scheduled" && <button className="underline" onClick={cancelDowngrade}>Cancel downgrade</button>}</p>}
        {data.billing_change_id && canManage && <Button variant="outline" onClick={() => setReviewTarget("__pending__")}>Review pending billing change</Button>}
        {data.payment_url && canManage && <a className="block underline" href={data.payment_url} target="_blank" rel="noopener noreferrer">Resolve payment with Stripe</a>}
        {data.billing_management === "stripe" && canManage && <Button variant="outline" size="sm" onClick={refreshStripe}>Refresh Stripe status</Button>}
      </div>

      {data.manual_checkout_available && (
        <div className="hard-card p-5 mb-6 space-y-3" data-testid="manual-billing-setup">
          <h2 className="font-semibold">Manual billing</h2>
          <p className="text-sm">Your {data.tiers.find((tier) => tier.id === data.plan)?.name} entitlement is currently managed by Fieldlink.</p>
          <p className="text-sm text-muted-foreground">Set up automatic monthly billing through Stripe to enable automatic renewals and plan upgrades.</p>
          <Button onClick={() => subscribe({ id: data.plan })} disabled={!canManage || !!paying}>
            {paying === data.plan ? "Opening Stripe…" : "Set up automatic billing"}
          </Button>
        </div>
      )}

      {/* LOCKED — paywall card */}
      {lockedReason && (
        <div
          className="mb-6 rounded-sm border-2 border-red-500 bg-red-50 dark:bg-red-950/40 p-5 flex items-start gap-4"
          data-testid="billing-paywall"
        >
          <div className="w-12 h-12 rounded-sm bg-red-600 text-white flex items-center justify-center shrink-0">
            <Lock className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-xl uppercase text-red-900 dark:text-red-200 tracking-tight">
              {data.lifecycle_state === "payment_locked" ? "Payment required" : LOCK_TITLES[lockedReason] || LOCK_TITLES.subscription_required}
            </div>
            <div className="mt-1 text-sm text-red-900 dark:text-red-100">
              {data.lifecycle_state === "payment_locked" ? "Your subscription needs attention. Your company data is safe. Update your payment details or restart your subscription where available." : LOCK_MESSAGES[lockedReason] || LOCK_MESSAGES.subscription_required}
            </div>
            <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-red-700 dark:text-red-300">
              Workspace access is paused. Your company data is safe.
            </div>
          </div>
        </div>
      )}

      {/* Trial countdown (non-blocking) */}
      {isTrialActive && trialDaysLeft != null && (
        <div
          className={`mb-6 rounded-sm border-2 p-4 flex items-start gap-3 ${
            trialDaysLeft <= 3
              ? "border-amber-500 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100"
              : "border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-100"
          }`}
          data-testid="billing-trial-banner"
        >
          <Calendar className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <span className="font-bold">{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left in your free Enterprise trial.</span>{" "}
            You have full access during your trial. Pick a plan below any time — no interruption, no setup, your data carries over.
          </div>
        </div>
      )}

      {/* Past-due grace (non-blocking) */}
      {isPastDueGrace && (
        <div
          className="mb-6 rounded-sm border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/40 p-4 flex items-start gap-3 text-amber-900 dark:text-amber-100"
          data-testid="billing-grace-banner"
        >
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <span className="font-bold">Payment didn&apos;t go through.</span>{" "}
            You have <span className="font-bold">{graceDaysLeft} day{graceDaysLeft === 1 ? "" : "s"}</span> to update your card before access is locked.
            {canManage && data.stripe_customer_id && (
              <Button onClick={openPortal} size="sm" className="ml-3 rounded-sm bg-amber-600 hover:bg-amber-700 text-white">
                Update card
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Polling banner */}
      {polling && (
        <div className="hard-card p-4 mb-6 border-blue-500 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-3" data-testid="billing-poll-banner">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <div className="text-sm text-blue-900 dark:text-blue-100">{pollMsg}</div>
        </div>
      )}

      {/* Current plan card */}
      <div className="hard-card p-6 !bg-[#09090B] text-white mb-6" data-testid="current-plan-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-tag !text-[#FF5F15]">Current plan</div>
            <div className="mt-1 flex items-center gap-3">
              <div className="font-display font-black text-4xl uppercase tracking-tight text-white" data-testid="current-plan-name">{data.plan_display_name || data.plan}</div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${
                cancelPending ? "bg-yellow-500 text-zinc-900" :
                data.plan_status === "active" ? "bg-emerald-500 text-zinc-900" :
                data.plan_status === "trial" ? "bg-blue-400 text-zinc-900" :
                data.plan_status === "past_due" ? "bg-yellow-400 text-zinc-900" :
                "bg-red-600 text-white"
              }`} data-testid="plan-status-badge">{cancelPending ? "ending" : data.plan_status}</span>
            </div>
            <div className="mt-3 text-sm text-zinc-300">
              <span className="font-mono">{data.plan === "trial" ? "Free trial" : data.monthly_price == null ? "Custom" : NZD.format(data.monthly_price)}</span><span className="text-zinc-500"> / month · NZD</span>
            </div>
            {periodEnd && (
              <div className="mt-2 flex items-center gap-2 text-xs text-zinc-400" data-testid="billing-period-end">
                <Calendar className="w-3.5 h-3.5" />
                {cancelPending
                  ? `Ends ${new Date(periodEnd).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}`
                  : `Next renewal ${new Date(periodEnd).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}`}
                {days != null && (
                  <span className="font-mono">· {days} day{days === 1 ? "" : "s"} left</span>
                )}
              </div>
            )}
            {!periodEnd && data.plan === "trial" && data.trial_ends_at && (
              <div className="mt-2 flex items-center gap-2 text-xs text-zinc-400" data-testid="billing-trial-end">
                <Calendar className="w-3.5 h-3.5" />
                {`Trial ends ${new Date(data.trial_ends_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}`}
                {trialDaysLeft != null && (
                  <span className="font-mono">· {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left</span>
                )}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="label-tag text-zinc-500">Active users</div>
            <div className="font-display font-black text-4xl" data-testid="active-users-count">{data.active_users}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1 flex items-center justify-end gap-1">
              <Users className="w-3 h-3" /> on your team
            </div>
          </div>
        </div>

        {data.tier_mismatch && (
          <div className="mt-5 rounded-sm border-2 border-amber-400 bg-amber-300/95 dark:bg-amber-900/40 dark:border-amber-600 p-3 flex items-start gap-2 text-zinc-900 dark:text-amber-100" data-testid="tier-mismatch-alert">
            <AlertTriangle className="w-4 h-4 text-amber-900 dark:text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm">
              You have <b>{data.active_users}</b> active users and a limit of <b>{data.user_limit}</b>. {data.next_plan === "fleet" ? "Contact support to review Fleet / custom capacity." : `Review the ${data.next_plan} plan before adding more users.`}
            </div>
          </div>
        )}

        {/* Manage row */}
        {canManage && data.plan !== "trial" && !data.checkout_available && (
          <div className="mt-5 space-y-3">
            <div className="flex items-center gap-3 rounded-sm border border-zinc-700 bg-zinc-900/60 p-3" data-testid="billing-autorenew-row">
              <Switch
                checked={!cancelPending}
                onCheckedChange={(v) => { if (v) { resumeSub(); } else { setCancelOpen(true); } }}
                data-testid="billing-autorenew-switch"
              />
              <div className="text-sm">
                <div className="font-bold text-white">Auto-renew {cancelPending ? "off" : "on"}</div>
                <div className="text-xs text-zinc-400">
                  {cancelPending
                    ? `Your card won't be charged again — access ends ${periodEnd ? new Date(periodEnd).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "at the period close"}. Flip back on any time before then.`
                    : "Your card is charged automatically each month so the crew never loses access. Turn off to stop at the end of the paid period."}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canManage && data.stripe_customer_id && (
                <Button
                  onClick={openPortal}
                  size="sm"
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white"
                  data-testid="billing-portal-btn"
                >
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Manage card & invoices in Stripe
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tier grid */}
      <TrialPlansMarker />
      <div className="label-tag mb-3">{data.plan === "trial" ? "Choose a plan to keep Fieldlink running" : "Plans"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.tiers.map((t) => {
          const Icon = TIER_ICONS[t.id] || HardHat;
          const isWhiteLabel = t.id === "white_label";
          const displayName = isWhiteLabel ? "White Label" : t.name;
          const isCurrent = data.plan === t.id;
          const isSuggested = data.suggested_tier?.id === t.id;
          const automatic = data.billing_management === "stripe" && !data.checkout_available && !data.legacy_custom && ["crew:site", "site:yard", "site:crew", "yard:site", "crew:white_label", "site:white_label", "yard:white_label", "white_label:yard", "white_label:site", "white_label:crew"].includes(`${data.plan}:${t.id}`);
          const downgrade = ["site:crew", "yard:site", "white_label:yard", "white_label:site", "white_label:crew"].includes(`${data.plan}:${t.id}`);
          const canCheckout = data.checkout_available && t.monthly_price != null;
          const ctaLabel = isCurrent && !canCheckout ? "Current plan" : automatic ? (downgrade ? "Review downgrade" : isWhiteLabel ? "Upgrade to White Label" : "Review upgrade") : canCheckout ? `Subscribe to ${displayName}` : "Contact Fieldlink";
          return (
            <div
              key={t.id}
              className={`hard-card p-5 flex flex-col ${isWhiteLabel ? "md:col-span-2" : ""} ${isCurrent ? "border-[#FF5F15] border-4" : isWhiteLabel ? "border-2 border-[#FF5F15]/60 dark:border-[#FF5F15]/70" : ""} ${isSuggested && !isCurrent ? "ring-2 ring-yellow-400" : ""}`}
              data-testid={`tier-card-${t.id}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-[#FF5F15] flex items-center justify-center rounded-sm">
                  <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div className={`font-display font-black uppercase tracking-tight text-zinc-900 dark:text-white ${isWhiteLabel ? "text-2xl" : "text-xl"}`}>{displayName}</div>
                {isWhiteLabel && <span className="ml-auto rounded-sm border border-[#FF5F15]/30 bg-[#FF5F15]/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-[#9A3500] dark:text-[#FFAB80]">Premium</span>}
              {isSuggested && <p className="text-xs text-zinc-600 dark:text-zinc-400">Fits your current team size</p>}
              </div>
              <div className="font-display font-black text-4xl text-zinc-900 dark:text-white">{t.monthly_price == null ? "Custom" : NZD.format(t.monthly_price)}{t.monthly_price != null && <span className="text-zinc-600 dark:text-zinc-400 text-sm font-mono font-normal"> /mo NZD</span>}</div>
              <div className="text-xs text-zinc-700 dark:text-zinc-300 font-mono uppercase tracking-widest mt-1">{t.id === "fleet" ? "50+ users" : t.blurb}</div>
              {(isWhiteLabel || t.description) && <p className="mt-3 text-sm font-semibold">{isWhiteLabel ? "Your brand. Powered by Fieldlink." : t.description}</p>}
              {isWhiteLabel ? (
                <ul className="mt-4 grid content-start gap-x-6 gap-y-2 sm:grid-cols-2 text-sm flex-1 text-zinc-700 dark:text-zinc-300">
                  {["Everything in Yard", "Your logo & company branding", "Custom brand colours", "Branded quotes & invoices", "Branded customer emails", "Remove optional Fieldlink branding", "Priority support"].map(feature => (
                    <li key={feature} className="flex items-start gap-2"><Check className="mt-0.5 w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" /><span>{feature}</span></li>
                  ))}
                </ul>
              ) : (
              <ul className="mt-4 space-y-1.5 text-sm flex-1 text-zinc-700 dark:text-zinc-300">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Unlimited timesheets</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Toolbox talks & pre-starts</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Team chat with @mentions</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Payroll export</li>
                {t.features?.map(feature => <li key={feature}>{feature}</li>)}
                {t.id !== "crew" && <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Priority support</li>}
                {t.id === "fleet" && <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Custom feature flags</li>}
              </ul>
              )}
              <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
                {t.monthly_price == null ? "Custom plan · contact us" : "Monthly plan · cancel anytime"}
              </div>
              <div className="mt-4">
                <Button
                  onClick={() => automatic ? setReviewTarget(t.id) : canCheckout ? subscribe(t) : window.location.assign("mailto:support@fieldlink.co.nz?subject=Fieldlink%20plan%20review")}
                  disabled={!canManage || paying === t.id || (isCurrent && !canCheckout) || (automatic && !!(data.billing_change_id || data.pending_downgrade))}
                  className={`w-full rounded-sm text-xs font-bold uppercase tracking-wider py-2 ${
                    isCurrent
                      ? "bg-zinc-200 hover:bg-zinc-300 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
                      : "bg-[#FF5F15] hover:bg-[#E65100] text-white"
                  }`}
                  data-testid={`tier-subscribe-${t.id}`}
                >
                  {paying === t.id ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Redirecting…</>
                  ) : (
                    <><CreditCard className="w-3.5 h-3.5 mr-1.5" /> {ctaLabel}</>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-xs text-zinc-700 dark:text-zinc-400 font-mono space-y-1">
        <div>Secure recurring payment via Stripe. NZD. Prices exclude GST.</div>
        <div>Subscriptions auto-renew every 30 days until cancelled. Cancel anytime — you keep access until the period closes.</div>
        {user.role === "super_admin" && <div>Super-admins can also move orgs between tiers from Site Admin → Organisations.</div>}
      </div>

      {/* Cancel confirmation */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Turn off auto-renew?</DialogTitle>
            <DialogDescription>
              Your card won&apos;t be charged again. You&apos;ll keep access until <b>{periodEnd ? new Date(periodEnd).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "the end of your period"}</b>, and you can flip auto-renew back on anytime before that.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-sm">Keep subscription</Button>
            <Button variant="destructive" onClick={cancelSub} disabled={cancelling} className="rounded-sm" data-testid="cancel-confirm">
              {cancelling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <XCircle className="w-4 h-4 mr-1.5" />}
              Confirm cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
